/* ============================================================================
   reco-session.js — the SESSION half of the recommendation system
   ("quick-adjust product recommendations" in the plan docs).

   The offline algorithm ranks what a customer is likely to buy from their
   PURCHASE HISTORY (served per-visitor by the recommendation service and
   stored by recommendations.js in localStorage.recommendedProducts). This
   file adjusts that state DURING the session from what the shopper is doing
   right now — pages viewed and cart contents — using the co-purchase
   correlation map (/pair-map: per product, its top related products by real
   co-purchase percentage; ~100KB, fetched once per session, cached 1h by the
   browser and 6h in sessionStorage).

   ISOLATION PRINCIPLE: own file, own storage keys (sessionStorage
   AGB_PairMap / AGB_RecoTrail / AGB_SessionRecs). Reads upstream state
   (recommendedProducts, SE_colorPref, /cart.js) and applies through ONE seam
   recommendations.js exposes (window.__AGB_applyRecs → its executePageUpdates,
   which keeps every panel-config surface gate). It NEVER writes
   localStorage.recommendedProducts — the offline state stays canonical, and
   session adjustments die with the tab (the docs' temporary-vs-persistent
   rule by construction).

   Blend rule (safeguards by design):
     base(i)    = 1 - i/N for the offline list (order-derived — robust to the
                  two score scales on the wire: fractions for real customers,
                  1..10 integers for synthetic ones)
     session(x) = Σ over signals s: weight(s) × pct(s → x), normalized to ≤1
                  weight: cart item 1.0, viewed page 0.6 × 0.8^recency-index
     final(x)   = base(x) + 0.9 × session(x)
   So the offline #1 (base 1.0) can only be displaced by a product that also
   has offline standing — a session signal alone reorders the tail, it cannot
   invent a new #1 for a known customer. For an ANONYMOUS shopper (no offline
   list) the session score IS the ranking — their first recommendations ever.
   Products in the cart are treated as owned: excluded from the output and
   feeding their correlations instead.

   Kill switch: window.AGB_RECO_SESSION_OFF = true  (or sessionStorage flag).
   Seam:        window.AGB_PAIR_MAP_URL overrides the map endpoint.
   ============================================================================ */
(function () {
    'use strict';

    try { if (window.AGB_RECO_SESSION_OFF === true) return; } catch (e) {}
    try { if (sessionStorage.getItem('AGB_RECO_SESSION_OFF') === '1') return; } catch (e) { return; }

    var MAP_URL = (typeof window.AGB_PAIR_MAP_URL === 'string' && window.AGB_PAIR_MAP_URL) ||
        'https://color-pref-432657747828.europe-west1.run.app/pair-map';
    var MAP_TTL_MS = 6 * 60 * 60 * 1000;
    var TRAIL_MAX = 20;
    var CART_W = 1.0;
    var VIEW_W = 0.6;
    var VIEW_DECAY = 0.8;
    var SESSION_GAIN = 0.9;
    var OUT_MAX = 8;

    // IDENTITY FENCE: the session state below (blended recs + the viewed-product
    // trail) describes ONE visitor. Impersonating a fake, exiting an
    // impersonation, or a late identity resolution changes who this browser is
    // mid-session - and without this the next page would keep serving the
    // PREVIOUS person's blended recommendations, and collection-reorder and
    // cart-suggestions would read them too. Owned here because this file owns
    // those keys (the impersonation harness lives in the other lane).
    (function fenceIdentity() {
        try {
            var vid = localStorage.getItem('viewerId') || localStorage.getItem('visitorID') || '';
            var was = sessionStorage.getItem('AGB_RecoVid');
            if (was !== null && was !== vid) {
                sessionStorage.removeItem('AGB_SessionRecs');
                sessionStorage.removeItem('AGB_RecoTrail');
                sessionStorage.removeItem('AGB_CartSuggestHtml');
            }
            sessionStorage.setItem('AGB_RecoVid', vid);
        } catch (e) {}
    })();

    function log() {
        try {
            if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
                console.log.apply(console, ['[reco-session]'].concat([].slice.call(arguments)));
            }
        } catch (e) {}
    }

    /* ---- the shopper's trail: viewed product pages, newest first ---------- */
    /* THE PANEL OWNS THIS (Dorian 2026-09-08: "all the rest where we are
       reordering the product should be bypassed"). recommendations.js already
       gates its own reordering on the scarcity config's `dynamicUI` switches;
       this file is the newer half of the same feature and answered only to its
       own kill switch, so turning reordering off in the panel left it running.
       Now one control governs every recommendation-driven reorder, and the
       colour switch (productCardColor) is deliberately NOT consulted - the
       colour personalisation stays on when reordering goes off.
       Unresolved reads as ON, which is the contract recommendations.js
       documents: the config arrives asynchronously and must never block. */
    function reorderTurnedOff() {
        try {
            var d = window.__AGB_dynamicUI;
            if (!d) return false;
            var f = d.features || d;
            return f.productReorder === false;
        } catch (e) { return false; }
    }
    function currentHandle() {
        var m = (location.pathname || '').match(/\/products\/([a-z0-9-]+)/);
        return m ? m[1] : null;
    }
    function readTrail() {
        try { return JSON.parse(sessionStorage.getItem('AGB_RecoTrail') || '[]') || []; }
        catch (e) { return []; }
    }
    function pushTrail(h) {
        var trail = readTrail();
        if (trail.length && trail[0] === h) return trail;   // same page reloaded
        trail = [h].concat(trail.filter(function (x) { return x !== h; })).slice(0, TRAIL_MAX);
        try { sessionStorage.setItem('AGB_RecoTrail', JSON.stringify(trail)); } catch (e) {}
        return trail;
    }

    /* ---- the correlation map: one fetch per session ----------------------- */
    function loadMap(cb) {
        try {
            var cached = JSON.parse(sessionStorage.getItem('AGB_PairMap') || 'null');
            if (cached && cached.at && (Date.now() - cached.at) < MAP_TTL_MS && cached.map) {
                cb(cached.map);
                return;
            }
        } catch (e) {}
        fetch(MAP_URL)
            .then(function (r) { if (!r.ok) throw new Error('pair-map ' + r.status); return r.json(); })
            .then(function (map) {
                if (!map || typeof map !== 'object') throw new Error('pair-map shape');
                try { sessionStorage.setItem('AGB_PairMap', JSON.stringify({ at: Date.now(), map: map })); }
                catch (e) { /* quota — serve from memory this page only */ }
                cb(map);
            })
            .catch(function (err) { log('map unavailable', String(err)); });
    }

    /* ---- current cart: the strongest signal + the owned-exclusion list ---- */
    function loadCart(cb) {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
                var handles = [];
                var items = (cart && cart.items) || [];
                for (var i = 0; i < items.length; i++) {
                    if (items[i] && items[i].handle && handles.indexOf(items[i].handle) < 0) {
                        handles.push(items[i].handle);
                    }
                }
                cb(handles);
            })
            .catch(function () { cb([]); });
    }

    function offlineList() {
        try { return JSON.parse(localStorage.getItem('recommendedProducts') || '[]') || []; }
        catch (e) { return []; }
    }

    function visitorColour() {
        // The window global is what color-pref.js APPLIED on this page - the
        // authoritative served colour. The localStorage cache stores the raw
        // service answer under .answer (shape: {vid, fetchedAt, answer:
        // {known, colors, owned}}), so fall back to that, accepting either
        // identity key the colour system caches under.
        try {
            var w = window.SE_colorPref;
            if (w && w.colors && w.colors.length) return String(w.colors[0]);
        } catch (e) {}
        try {
            var cp = JSON.parse(localStorage.getItem('SE_colorPref') || 'null');
            var colors = cp && cp.answer && cp.answer.colors;
            if (!colors || !colors.length) return null;
            var vid = '';
            var viewer = '';
            try { vid = localStorage.getItem('visitorID') || ''; } catch (e) {}
            try { viewer = localStorage.getItem('viewerId') || ''; } catch (e) {}
            if (cp.vid && cp.vid !== vid && cp.vid !== viewer) return null;
            return String(colors[0]);
        } catch (e) {}
        return null;
    }

    function blend(map, trail, cartHandles) {
        var offline = offlineList();
        var here = currentHandle();
        var excluded = {};
        var i;
        for (i = 0; i < cartHandles.length; i++) excluded[cartHandles[i]] = 1;   // owned assumption
        if (here) excluded[here] = 1;                                            // never recommend this page

        // session contributions
        var session = {};   // handle -> {score, t, c}
        function contribute(fromHandle, weight) {
            var related = map[fromHandle];
            if (!related) return;
            for (var j = 0; j < related.length; j++) {
                var r = related[j];
                if (!r || !r.h || excluded[r.h]) continue;
                var cur = session[r.h] || { score: 0, t: r.t || '', c: r.c || null };
                cur.score += weight * (r.p || 0);
                session[r.h] = cur;
            }
        }
        for (i = 0; i < cartHandles.length; i++) contribute(cartHandles[i], CART_W);
        for (i = 0; i < trail.length; i++) contribute(trail[i], VIEW_W * Math.pow(VIEW_DECAY, i));

        var maxSession = 0;
        var h;
        for (h in session) { if (session[h].score > maxSession) maxSession = session[h].score; }

        // merge: offline order gives the base; session lifts and extends
        var final = {};   // handle -> {score, entry}
        var n = offline.length;
        for (i = 0; i < n; i++) {
            var o = offline[i];
            if (!o || !o.productLink || excluded[o.productLink]) continue;
            if (o.productLink in final) continue;   // duplicate handle: FIRST (best rank) wins
            final[o.productLink] = { score: 1 - (i / Math.max(n, 1)), entry: o };
        }
        for (h in session) {
            var lift = maxSession > 0 ? (SESSION_GAIN * session[h].score / maxSession) : 0;
            if (final[h]) {
                final[h].score += lift;
            } else if (lift > 0.15 && session[h].score >= 0.03) {
                // A session-only candidate needs BOTH relative strength (top of
                // this session's signals) and an absolute floor (>=3% raw
                // co-purchase echo) - max-normalization alone would promote the
                // strongest of even negligible signals.
                final[h] = {
                    score: lift,
                    entry: {
                        productLink: h,
                        score: null,
                        collection: session[h].c,
                        camo: visitorColour(),
                        productName: session[h].t || h
                    }
                };
            }
        }

        var out = [];
        for (h in final) out.push({ h: h, s: final[h].score, e: final[h].entry });
        out.sort(function (a, b) { return b.s - a.s || (a.h < b.h ? -1 : 1); });
        out = out.slice(0, OUT_MAX);
        return out.map(function (x) {
            var e = x.e;
            e.score = Math.round(x.s * 1000) / 1000;
            return e;
        });
    }

    function sameOrder(a, b) {
        if (a.length !== b.length) return false;
        for (var i = 0; i < a.length; i++) {
            if ((a[i] && a[i].productLink) !== (b[i] && b[i].productLink)) return false;
        }
        return true;
    }

    function listHash(list) {
        var joined = list.map(function (x) { return x.productLink; }).join('|');
        var hash = 5381;
        for (var j = 0; j < joined.length; j++) hash = ((hash << 5) + hash + joined.charCodeAt(j)) | 0;
        return ('00000000' + (hash >>> 0).toString(16)).slice(-8);
    }

    function run() {
        // Nothing downstream would use the blend, and it costs a ~100KB map
        // fetch, so stand down rather than compute an answer nobody reads.
        if (reorderTurnedOff()) { log('panel switch off - not blending'); return; }
        var here = currentHandle();
        var trail = here ? pushTrail(here) : readTrail();
        loadMap(function (map) {
            loadCart(function (cartHandles) {
                if (!trail.length && !cartHandles.length) { log('no session signals yet'); return; }
                var offline = offlineList().slice(0, OUT_MAX);
                var blended = blend(map, trail, cartHandles);
                if (!blended.length) { log('nothing to apply'); return; }
                try { sessionStorage.setItem('AGB_SessionRecs', JSON.stringify(blended)); } catch (e) {}
                var changed = !sameOrder(blended, offline);
                // Emit ALWAYS (changed:0 = "session agreed with offline") so
                // every page leaves a session-side record - a missing row would
                // be indistinguishable from the blender not running at all.
                try {
                    if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
                        window.CAEDev.emit('reco_adjust', {
                            n: blended.length,
                            list_hash: listHash(blended),
                            trail_n: trail.length,
                            cart_n: cartHandles.length,
                            had_offline: offline.length ? 1 : 0,
                            changed: changed ? 1 : 0
                        });
                    }
                } catch (e) {}
                if (!changed) { log('session agrees with offline order'); return; }
                // Do not repaint what is already on screen: run() can fire from
                // boot AND from every 'agb:recs-applied', and each apply drives
                // the full surface chain (including a per-card image pass).
                var blendHash = listHash(blended);
                if (blendHash === _lastAppliedHash) { log('already applied this blend'); return; }
                _lastAppliedHash = blendHash;
                if (typeof window.__AGB_applyRecs === 'function') {
                    // detail for the preview explainer toast (read by the
                    // recoApplied emit inside executePageUpdates)
                    try {
                        window.__AGB_sessionBlendInfo = {
                            trail: trail.slice(0, 3).join(', ') + (trail.length > 3 ? '…' : ''),
                            cart: cartHandles.length
                        };
                    } catch (e) {}
                    window.__AGB_applyRecs(blended,
                        (function () { try { return localStorage.getItem('tande'); } catch (e) { return null; } })(),
                        (function () { try { return localStorage.getItem('country'); } catch (e) { return null; } })());
                    log('applied session-adjusted order', blended.map(function (x) { return x.productLink; }));
                } else {
                    log('apply seam missing — state stored, nothing re-rendered');
                }
            });
        });
    }

    // After recommendations.js + the reorder surfaces settle (they run within
    // ~3s of load); the blender then adjusts on top of the fresh offline state.
    // When the OFFLINE apply lands later than that (slow fetch / cold legacy
    // backend), it overwrites the blended render - so re-blend on its
    // announcement, against the now-fresh offline list. Capped so a pathological
    // ping-pong is impossible; our own applies are marked and ignored.
    var _lastAppliedHash = '';
    var _reblends = 0;
    document.addEventListener('agb:recs-applied', function (e) {
        try { if (e && e.detail && e.detail.session) return; } catch (err) {}
        if (_reblends >= 2) return;
        _reblends++;
        setTimeout(run, 300);
    });
    function boot() { setTimeout(run, 3500); }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
